import React, { useMemo } from 'react';
import {
  Autocomplete,
  AutocompleteOption,
  Avatar,
  Chip,
  ChipDelete,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from '@mui/joy';
import { userDisplayName, userInitials, type DisplayUser } from '../../utils/userDisplay';

export type SelectableUser = DisplayUser;

interface UserMultiSelectProps {
  /** Selected user IDs, in display order. */
  value: number[];
  onChange: (userIds: number[]) => void;
  /** Assignable users (normally the org's active users). */
  users: SelectableUser[];
  /**
   * Users already selected that may be missing from `users` — e.g. a
   * deactivated rep still on a deal. They render as chips so they can be
   * removed, but are never offered as new options.
   */
  selectedFallback?: SelectableUser[];
  /** IDs that can't be picked here (e.g. the primary rep). */
  excludeIds?: number[];
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  loading?: boolean;
  disabled?: boolean;
  maxSelected?: number;
}

/**
 * Multi-select of org users with avatar chips. Joy's Autocomplete supplies
 * the keyboard model, listbox semantics and chip removal.
 */
export const UserMultiSelect: React.FC<UserMultiSelectProps> = ({
  value,
  onChange,
  users,
  selectedFallback = [],
  excludeIds = [],
  label = 'Users',
  placeholder = 'Add people…',
  helperText,
  error,
  loading = false,
  disabled = false,
  maxSelected,
}) => {
  const byId = useMemo(() => {
    const map = new Map<number, SelectableUser>();
    selectedFallback.forEach((user) => map.set(user.id, user));
    users.forEach((user) => map.set(user.id, user));
    return map;
  }, [users, selectedFallback]);

  const selected = useMemo(
    () => value.map((id) => byId.get(id)).filter((user): user is SelectableUser => !!user),
    [value, byId],
  );

  const options = useMemo(() => {
    const excluded = new Set(excludeIds);
    const pickable = users.filter((user) => !excluded.has(user.id) && user.is_active !== false);
    const pickableIds = new Set(pickable.map((user) => user.id));
    // Selected users stay in the options so Autocomplete can match its value.
    return [...pickable, ...selected.filter((user) => !pickableIds.has(user.id))];
  }, [users, excludeIds, selected]);

  const atLimit = maxSelected !== undefined && value.length >= maxSelected;
  const noOneToAdd = !loading && users.filter((u) => !excludeIds.includes(u.id)).length === 0;

  return (
    <FormControl error={!!error} disabled={disabled}>
      <FormLabel>{label}</FormLabel>
      <Autocomplete
        multiple
        value={selected}
        options={options}
        loading={loading}
        disabled={disabled || (noOneToAdd && selected.length === 0)}
        placeholder={loading ? 'Loading team…' : selected.length ? '' : placeholder}
        getOptionLabel={userDisplayName}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        getOptionDisabled={(option) => (atLimit && !value.includes(option.id)) || option.is_active === false}
        filterSelectedOptions
        onChange={(_, next) => onChange(next.map((user) => user.id))}
        slotProps={{ listbox: { sx: { zIndex: 9999 } } }}
        renderOption={(props, user) => (
          <AutocompleteOption {...props} key={user.id}>
            <ListItemDecorator>
              <Avatar size="sm">{userInitials(user)}</Avatar>
            </ListItemDecorator>
            <ListItemContent>
              <Typography level="body-sm">{userDisplayName(user)}</Typography>
              {user.email && (
                <Typography level="body-xs" textColor="text.tertiary">
                  {user.email}
                </Typography>
              )}
            </ListItemContent>
          </AutocompleteOption>
        )}
        renderTags={(tags, getTagProps) =>
          tags.map((user, index) => {
            const { onClick, ...tagProps } = getTagProps({ index });
            const inactive = user.is_active === false;
            return (
              <Chip
                {...tagProps}
                key={user.id}
                variant="soft"
                color={inactive ? 'neutral' : 'primary'}
                startDecorator={<Avatar size="sm" sx={{ '--Avatar-size': '18px', fontSize: '0.6rem' }}>{userInitials(user)}</Avatar>}
                endDecorator={
                  <ChipDelete onClick={onClick} aria-label={`Remove ${userDisplayName(user)}`} />
                }
                sx={inactive ? { opacity: 0.7 } : undefined}
              >
                {userDisplayName(user)}
                {inactive ? ' (inactive)' : ''}
              </Chip>
            );
          })
        }
      />
      {(error || helperText || noOneToAdd) && (
        <FormHelperText>
          {error || (noOneToAdd ? 'Invite teammates in Settings → Users to add them here.' : helperText)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default UserMultiSelect;
